import { FormikProvider, useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import FormError from "../../Tags/FormError";
import { createTeam, updateTeam } from "../../../apis/workspace/teams";
import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import Image from "../../Tags/Image";
import Input from "../../Tags/Input";
import { Button } from "../../ui/button";
import UserMultiSelect from "../shared/UserMultiSelect";
import { useCheckTeamMembersConflicts } from "../../../apiHooks/useCheckTeamMembersConflicts";

export default function TeamModal({
  closeModal,
  edit = false,
  data = {},
  teamMembers = [],
  activeWorkspace,
  refetch = () => undefined,
}) {
  const queryClient = useQueryClient();
  const workspaceId = activeWorkspace?.id;

  const getIds = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => item && item.id).filter(Boolean);
  };

  const initialIds = useMemo(() => getIds(data?.members), [data?.members]);

  const [selectedIds, setSelectedIds] = useState(initialIds);
  const [conflictMembers, setConflictMembers] = useState({});
  const [hasConflicts, setHasConflicts] = useState(false);
  const conflictCheckRequestId = useRef(0);

  const { mutateAsync: checkTeamMemberConflicts, isLoading: isCheckingTeamMemberConflicts } =
    useCheckTeamMembersConflicts({
      onError: (error) => {
        const message = error?.response?.data?.message ?? "Failed to check team member conflicts.";
        toast.error(message);
      },
    });

  const runConflictCheck = useCallback(
    async (memberIds = []) => {
      if (edit) return;

      const safeMemberIds = Array.isArray(memberIds) ? memberIds : [];
      conflictCheckRequestId.current += 1;
      const requestId = conflictCheckRequestId.current;

      if (!workspaceId || safeMemberIds.length === 0) {
        setConflictMembers({});
        setHasConflicts(false);
        return;
      }

      try {
        const response = await checkTeamMemberConflicts({
          workspaceId,
          teamId: data?.id,
          teamMembers: safeMemberIds,
        });

        if (conflictCheckRequestId.current !== requestId) {
          return;
        }

        const conflicts =
          response?.data?.conflicts ??
          response?.data?.data?.conflicts ??
          response?.conflicts ??
          response?.data ??
          [];

        const conflictEntries = Array.isArray(conflicts)
          ? conflicts
              .map((conflict) => {
                const memberId =
                  conflict?.user?.id ||
                  conflict?.team_member?.id ||
                  conflict?.member?.id ||
                  conflict?.member_id ||
                  conflict?.team_member_id ||
                  conflict?.id;
                const teamName = conflict?.current_team?.name || conflict?.team?.name || "another";
                if (typeof memberId === "string" && memberId.length > 0) {
                  return [memberId, teamName];
                }
                return null;
              })
              .filter(Boolean)
          : [];

        const conflictMap = Object.fromEntries(conflictEntries);
        setConflictMembers(conflictMap);
        setHasConflicts(conflictEntries.length > 0);
      } catch (error) {
        if (conflictCheckRequestId.current === requestId) {
          setConflictMembers({});
          setHasConflicts(false);
        }
      }
    },
    [edit, workspaceId, data?.id, checkTeamMemberConflicts]
  );

  useEffect(() => {
    setSelectedIds(initialIds);
    if (!edit) {
      runConflictCheck(initialIds);
    } else {
      setConflictMembers({});
      setHasConflicts(false);
    }
  }, [initialIds, edit, runConflictCheck]);

  const formInitialValues = useMemo(
    () => ({
      name: data?.name ?? "",
      team_members: initialIds,
      description: data?.description ?? "",
    }),
    [data?.name, data?.description, initialIds]
  );

  const handleModalClose = () => {
    formik.resetForm();
    setSelectedIds(initialIds);
    setConflictMembers({});
    setHasConflicts(false);
    conflictCheckRequestId.current += 1;
    closeModal();
  };

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .required("Please enter team name")
        .max(100, "Team can not be more than 100 characters"),
      description: Yup.string()
        .required("Please enter description")
        .max(300, "Description can not be more than 300 characters"),
    }),
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        description: values.description,
        team_members: values.team_members,
      };

      if (edit) {
        updateTeamFn.mutate({
          data: {
            name: values.name,
            description: values.description,
          },
          teamId: data && data.id,
          options: {
            headers: { "X-Workspace-Id": workspaceId },
          },
        });
        return;
      }

      createTeamFn.mutate({
        data: payload,
        options: {
          headers: { "X-Workspace-Id": workspaceId },
        },
      });
    },
  });

  useEffect(() => {
    formik.setFieldValue("team_members", selectedIds, false);
  }, [formik, selectedIds]);

  const handleMembersChange = (ids = []) => {
    if (!Array.isArray(ids)) return;
    const uniqueIds = [...new Set(ids)];
    setSelectedIds(uniqueIds);
    runConflictCheck(uniqueIds);
  };

  const createTeamFn = useMutation("create_team", createTeam, {
    onSuccess(dataResponse) {
      toast.success(dataResponse?.message);
      handleModalClose();
      queryClient.invalidateQueries("get_teams");
      formik.resetForm();
      formik.setSubmitting(false);
    },
    onError() {
      formik.setSubmitting(false);
    },
  });

  const updateTeamFn = useMutation("update_team", updateTeam, {
    onSuccess(dataResponse) {
      toast.success(dataResponse?.message);
      handleModalClose();
      queryClient.invalidateQueries("get_teams");
      formik.resetForm();
      formik.setSubmitting(false);
      if (refetch) {
        refetch();
      }
    },
    onError() {
      formik.setSubmitting(false);
    },
  });

  return (
    <>
      <div className="flex items-center space-x-4 mb-2">
        <div className="inline-flex justify-center border border-border rounded-[--radius] p-2 bg-background">
          <Image src="/images/icons/untitledui/group.svg" width="20" className="inline-block" />
        </div>
        <div>
          <h2 className="text-base font-semibold leading-7 ">{edit ? "Update" : "Create"} Team</h2>
          <p className="text-paragraph">Please enter a name for this team.</p>
        </div>
      </div>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <div className="py-3">
            <div className="mb-5">
              <label htmlFor="name" className="inline-block mb-2 text-sm font-medium leading-6">
                Team name
              </label>
              <Input
                type="text"
                name="name"
                id="name"
                autoComplete="name"
                placeholder="e.g. BDR Sales"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    formik.handleSubmit();
                  }
                }}
              />
              <FormError field="name" />
            </div>
            {!edit && (
              <div className="mb-5">
                <label className="inline-block mb-2 text-sm font-medium leading-6">Members</label>
                <div>
                  <UserMultiSelect
                    users={teamMembers}
                    selectedIds={selectedIds}
                    onChange={handleMembersChange}
                    disabled={updateTeamFn.isLoading || createTeamFn.isLoading}
                    loading={false}
                    warningIds={Object.keys(conflictMembers)}
                    warningDetails={conflictMembers}
                    error={false}
                    placeholder="Select users"
                    emptyMessage="No users found"
                    errorMessage="Error loading users"
                  />
                  {/* {isCheckingTeamMemberConflicts && (
                    <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                      Checking selected members for conflicts...
                    </div>
                  )} */}
                  {hasConflicts && (
                    <div className="mt-2  text-warning-700">
                      Some users are part of other teams. Verify before proceeding.
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="mb-5">
              <label htmlFor="description" className="inline-block mb-2 text-sm font-medium leading-6">
                Description
              </label>
              <Input
                type="text"
                name="description"
                id="description"
                autoComplete="description"
                placeholder="Business team with access to contact enrichment only"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.description}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    formik.handleSubmit();
                  }
                }}
              />
              <FormError field="description" />
            </div>
          </div>
          <div className="mt-0 space-x-2 flex">
            <Button
              className="flex-1"
              onClick={(e) => {
                e.preventDefault();
                handleModalClose();
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={formik.isSubmitting || !formik.isValid} className="flex-1">
              {edit ? "Update Team" : "Create Team"}
              {formik.isSubmitting ? <div className="loader_circle ml-3"></div> : ""}
            </Button>
          </div>
        </form>
      </FormikProvider>
    </>
  );
}
